import { XBlackIcon } from '@assets/iconComponents';
import { Dialog, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Checkbox, Text } from '..';
import { colorSet } from '@theme';
import { useFetchImageIssueReportList } from '@api';
import { IIssueReportListItem, IUploadImageListItem } from '@types';

interface IAdminIssueReportModalProps {
  isVisible: boolean;
  onClose: () => void;
  focusedImage: IUploadImageListItem | null;
  getImageIssueReportList: (imageId: number, issueReportList: IIssueReportListItem[]) => void;
  updateCheckedIssueReportIds: (imageId: number, checkedIssueReportId: number) => void;
}

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const IssueReportList = styled('div')({
  width: '100%',
  maxHeight: 'calc(100vh - 224px)',
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  },

  '&.show-scrollbar': {
    paddingRight: '6px'
  }
});

export const AdminIssueReportModal = (props: IAdminIssueReportModalProps) => {
  const { isVisible, onClose, focusedImage, getImageIssueReportList, updateCheckedIssueReportIds } = props;

  // 미해결 리포트 리스트의 스크롤바 여부에 따른 CSS 분기를 위한 상태
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const issueReportListRef = useRef<HTMLDivElement>(null);

  const needFetch = isVisible && focusedImage && !focusedImage.issueReportList; // 모달이 보여지며, focus된 이미지에 대해 오류 리포트 리스트를 fetch한 적이 없는 경우

  const { data: issueReportListData } = useFetchImageIssueReportList(needFetch ? focusedImage?.imageId : undefined);

  // 미해결 리포스 리스트의 스크롤바 존재 여부 체크
  useEffect(() => {
    const checkScrollbar = () => {
      if (issueReportListRef.current) {
        const element = issueReportListRef.current;
        const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;
        setHasScrollbar(hasVerticalScrollbar);
      }
    };

    // 초기 체크를 약간 지연시켜 DOM이 완전히 렌더링된 후 실행
    const timeoutId = setTimeout(checkScrollbar, 100);

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', checkScrollbar);

    // MutationObserver로 DOM 변화 감지
    const observer = new MutationObserver(checkScrollbar);
    if (issueReportListRef.current) {
      observer.observe(issueReportListRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScrollbar);
      observer.disconnect();
    };
  }, [isVisible]);

  useEffect(() => {
    if (issueReportListData && focusedImage && focusedImage.imageId && !focusedImage.issueReportList) {
      getImageIssueReportList(focusedImage.imageId, issueReportListData);
    }
  }, [issueReportListData]);

  return (
    <Dialog
      open={isVisible}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent'
        },
        '& .MuiDialog-paper': {
          width: 512,
          minHeight: 160,
          maxHeight: 'calc(100% - 120px)',
          margin: '60px 0px',
          boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
          borderRadius: 0
        }
      }}
    >
      <div
        style={{
          width: '100%',
          height: '64px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '24px 24px 12px 24px'
        }}
      >
        <Text
          variant="h3"
          fontWeight="
        bold"
          color={colorSet.gray500}
        >
          오류 리포트
        </Text>
        <XBlackIcon style={{ width: 24, height: 24, cursor: 'pointer' }} onClick={onClose} />
      </div>

      <div
        style={{
          padding: '0px 24px 40px 24px'
        }}
      >
        <IssueReportList ref={issueReportListRef} className={hasScrollbar ? 'show-scrollbar' : ''}>
          <div>
            {focusedImage && focusedImage.issueReportList && focusedImage.issueReportList.length > 0
              ? focusedImage.issueReportList.map((item, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        padding: '12px 0px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '12px',
                        borderBottom: `1px solid ${colorSet.gray200}`
                      }}
                    >
                      <Checkbox
                        type="circle"
                        checked={focusedImage?.checkedIssueReportIds?.includes(item.id)}
                        onClick={() => {
                          if (focusedImage && focusedImage.imageId) {
                            updateCheckedIssueReportIds(focusedImage.imageId, item.id);
                          }
                        }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Text
                          variant="body1"
                          color={
                            focusedImage?.checkedIssueReportIds?.includes(item.id) ? colorSet.gray300 : colorSet.gray500
                          }
                        >
                          {item.reportContent}
                        </Text>
                        <Text
                          variant="body2"
                          color={
                            focusedImage?.checkedIssueReportIds?.includes(item.id) ? colorSet.gray300 : colorSet.gray400
                          }
                        >
                          {item.createdAt}
                        </Text>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </IssueReportList>
      </div>
    </Dialog>
  );
};
